import React, {ForwardedRef, forwardRef, useEffect, useImperativeHandle, useRef, useState} from "react";
import {ElementDto} from "../../../../../common/apis/element/ElementDto";
import {AttributeDefinitionDto} from "../../../../../common/apis/stereotype/AttributeDefinitionDto";
import {ReferenceDefinitionDto} from "../../../../../common/apis/stereotype/ReferenceDefinitionDto";
import {StereotypeLayoutDefinitionDto} from "../../../../../common/apis/stereotype/StereotypeLayoutDefinitionDto";
import {t} from "../../../../../store/localization/TranslMessasge";
import {ErrorTranslationKey} from "../../ErrorTranslationKey";
import Snackbar from "../../snackbar/Snackbar";
import {AttributeValueValidator} from "../attributes/AttributeValueValidator";
import {UiComponent, UiComponentSchema} from "./model/UiComponent";
import {CheckboxFieldPlugin} from "./plugin/CheckboxFieldPlugin";
import {DatePickerFieldPlugin} from "./plugin/DatePickerFieldPlugin";
import {DateTimePickerFieldPlugin} from "./plugin/DateTimePickerFieldPlugin";
import {GridLayoutContainerPlugin} from "./plugin/GridLayoutContainerPlugin";
import {NumberFieldPlugin} from "./plugin/NumberFieldPlugin";
import {SelectFieldPlugin} from "./plugin/SelectFieldPlugin";
import {SmartReferenceListFieldPlugin} from "./plugin/SmartReferenceListFieldPlugin";
import {SmartReferenceSelectFieldPlugin} from "./plugin/SmartReferenceSelectFieldPlugin";
import {SwitchFieldPlugin} from "./plugin/SwitchFieldPlugin";
import {TextFieldPlugin} from "./plugin/TextFieldPlugin";
import {TimePickerFieldPlugin} from "./plugin/TimePickerFieldPlugin";
import {
    UiComponentDataContext,
    UiComponentPersistenceContext,
    UiComponentRendererPlugin,
    UiComponentRenderingContext,
    UiComponentStateContext,
    UiComponentValidationContext
} from "./UiComponentRendererPlugin";
import {UiComponentRendererPluginRegistry} from "./UiComponentRendererPluginRegistry";


const componentRendererRegistry = new UiComponentRendererPluginRegistry();
componentRendererRegistry.registerPlugin(new CheckboxFieldPlugin());
componentRendererRegistry.registerPlugin(new DatePickerFieldPlugin());
componentRendererRegistry.registerPlugin(new DateTimePickerFieldPlugin());
componentRendererRegistry.registerPlugin(new GridLayoutContainerPlugin());
componentRendererRegistry.registerPlugin(new NumberFieldPlugin());
componentRendererRegistry.registerPlugin(new SelectFieldPlugin());
componentRendererRegistry.registerPlugin(new SmartReferenceListFieldPlugin());
componentRendererRegistry.registerPlugin(new SmartReferenceSelectFieldPlugin());
componentRendererRegistry.registerPlugin(new SwitchFieldPlugin());
componentRendererRegistry.registerPlugin(new TextFieldPlugin());
componentRendererRegistry.registerPlugin(new TimePickerFieldPlugin());

export interface StereotypeLayoutRendererApi {
    save: () => Promise<boolean>;
    reset: () => void;
}

export interface StereotypeLayoutRendererProps {
    element: ElementDto;
    editMode?: boolean;
    layoutDefinition: StereotypeLayoutDefinitionDto;
    attributeDefinitions: AttributeDefinitionDto[];
    referenceDefinitions: ReferenceDefinitionDto[];
}

const StereotypeLayoutRenderer = forwardRef<StereotypeLayoutRendererApi, StereotypeLayoutRendererProps>(
        (props: Readonly<StereotypeLayoutRendererProps>, ref: ForwardedRef<StereotypeLayoutRendererApi>) => {
    const {element, editMode = false, layoutDefinition, attributeDefinitions, referenceDefinitions} = props;
    const attributeDefinitionMap = Object.fromEntries(attributeDefinitions.map(attr => [attr.id, attr]));
    const referenceDefinitionMap = Object.fromEntries(referenceDefinitions.map(ref => [ref.id, ref]));

    const attributeValueValidator = useRef(new AttributeValueValidator());

    const [layout, setLayout] = useState<UiComponent | undefined>(undefined);
    const [renderer, setRenderer] = useState<UiComponentRendererPlugin | undefined>(undefined);
    const [values, setValues] = useState<Record<string, any>>([]);
    const [errors, setErrors] = useState<Record<string, string>>({});

    useImperativeHandle(ref, () => ({
        save: () => validateAndSave(),
        reset: () => reset()
    }));

    useEffect(() => {
        const layout = validateSchema(layoutDefinition.layout, layoutDefinition.version);
        setLayout(layout);

        const renderer = componentRendererRegistry.getPlugin(layout.componentType);
        setRenderer(renderer);

        if (renderer) {
            const dataContext: UiComponentDataContext = {
                element: element,
                attributeDefinitions: Object.fromEntries(attributeDefinitions.map(attr => [attr.id, attr])),
                referenceDefinitions: Object.fromEntries(referenceDefinitions.map(ref => [ref.id, ref]))
            };
            const renderingContext: UiComponentRenderingContext = {
                rendererPluginRegistry: componentRendererRegistry
            };
            renderer.getInitialValues(layout, dataContext, renderingContext)
                    .then((initialValues) => setValues(initialValues));
        }
    }, [element, layoutDefinition, attributeDefinitions, referenceDefinitions]);

    function onValuesChange(changedValues: Record<string, any>) {
        setValues({...values, ...changedValues});
    }

    async function validateAndSave(): Promise<boolean> {
        try {
            if (layout) {
                const plugin = componentRendererRegistry.getPlugin(layout.componentType);
                if (plugin) {
                    const dataContext: UiComponentDataContext = {
                        element: element,
                        attributeDefinitions: attributeDefinitionMap,
                        referenceDefinitions: referenceDefinitionMap
                    };
                    const stateContext: UiComponentStateContext = {
                        editMode: editMode,
                        values: values,
                        onValuesChange: () => {},
                        errors: errors
                    };
                    const validationContext: UiComponentValidationContext = {
                        pluginRegistry: componentRendererRegistry,
                        valueValidator: attributeValueValidator.current
                    };
                    const validationErrors = plugin.validate(layout, dataContext, stateContext, validationContext);
                    if (Object.keys(validationErrors).length > 0) {
                        setErrors(validationErrors);
                        return Promise.resolve(false);
                    } else {
                        setErrors({});
                        const persistenceContext: UiComponentPersistenceContext = {
                            pluginRegistry: componentRendererRegistry,
                        };
                        const promises = plugin.save(layout, dataContext, stateContext, persistenceContext);
                        return Promise.allSettled(promises)
                                .then((results) => {
                                    return true;
                                });
                    }
                }
            }
            return Promise.resolve(false);
        } catch (error) {
            Snackbar.error(t(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED), error);
            return Promise.resolve(false);
        }
    }

    function reset() {
        const dataContext: UiComponentDataContext = {
            element: element,
            attributeDefinitions: attributeDefinitionMap,
            referenceDefinitions: referenceDefinitionMap
        };
        renderer?.getInitialValues(layout, dataContext, {rendererPluginRegistry: componentRendererRegistry})
                .then((initialValues) => setValues(initialValues));
        setErrors({});
    }

    if (!layout || !renderer) {
        return null;
    }

    return (
            <>
                {
                    renderer.renderComponent({
                        component: layout,
                        parentComponent: undefined,
                        dataContext: {
                            element: element,
                            attributeDefinitions: attributeDefinitionMap,
                            referenceDefinitions: referenceDefinitionMap
                        },
                        stateContext: {
                            editMode: editMode,
                            values: values,
                            onValuesChange: onValuesChange,
                            errors: errors
                        },
                        renderingContext: {
                            rendererPluginRegistry: componentRendererRegistry
                        }
                    })
                }
            </>
    );
});

function validateSchema(jsonObject: Object, version: string): UiComponent {
    if (version !== "1") {
        throw new Error("Unsupported layout version");
    }
    return UiComponentSchema.parse(jsonObject);
}

export default StereotypeLayoutRenderer;